import { PageHeader } from '@/shared/components/PageHeader';
import { showErrorToast } from '@/shared/notifications/toast-utils';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import type { AuditTrailSearchQuery } from '@skewb-works/contracts';
import { Download, History, Home } from 'lucide-react';
import { useState } from 'react';
import { AuditTrailFilters } from '../components/AuditTrailFilters';
import { AuditTrailPagination } from '../components/AuditTrailPagination';
import { AuditTrailTable } from '../components/AuditTrailTable';
import { useAuditTrailSearch } from '../hooks/useAuditTrail';
import { downloadFile, formatAuditTrailCsv, formatAuditTrailJson } from '../utils/auditTrailExport';

function getDefaultDates() {
  const today = new Date().toISOString().slice(0, 10);
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  return { from_date: sevenDaysAgo, to_date: today };
}

export default function AuditTrailPage() {
  const defaultDates = getDefaultDates();

  const [filters, setFilters] = useState<Partial<AuditTrailSearchQuery>>({
    limit: 50,
    skip: 0,
    ...defaultDates,
  });

  const hasRequiredDates = filters.from_date !== undefined && filters.to_date !== undefined;
  const { data, isLoading, isError } = useAuditTrailSearch(filters, hasRequiredDates);

  const items = data?.data ?? [];
  const total = data?.total ?? 0;
  const limit = filters.limit ?? 50;
  const skip = filters.skip ?? 0;

  function handleExportCsv() {
    if (items.length === 0) {
      showErrorToast('No data exists for the selected period');
      return;
    }
    const csv = formatAuditTrailCsv(items);
    downloadFile(csv, `audit-trail-${new Date().toISOString().slice(0, 10)}.csv`, 'text/csv');
  }

  function handleExportJson() {
    if (items.length === 0) {
      showErrorToast('No data exists for the selected period');
      return;
    }
    const json = formatAuditTrailJson(items);
    downloadFile(
      json,
      `audit-trail-${new Date().toISOString().slice(0, 10)}.json`,
      'application/json',
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <PageHeader
        title="Audit Trail"
        subtitle="Search and export system activity logs"
        icon={<History className="w-8 h-8" style={{ color: '#E84A2B' }} />}
        breadcrumbs={[
          { label: <Home className="w-4 h-4" />, href: '/', current: false },
          { label: 'Settings', href: '/settings', current: false },
          { label: 'Audit Trail', current: true },
        ]}
        actions={
          total > 0 && items.length > 0 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <Download className="w-4 h-4" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleExportCsv}>Export as CSV</DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportJson}>Export as JSON</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : undefined
        }
      />

      <AuditTrailFilters filters={filters} onFilterChange={setFilters} />

      <div className="flex-1 overflow-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="bg-card border border-border rounded-lg p-6 w-full max-w-2xl mx-6">
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#E84A2B]" />
                <span className="ml-3 text-muted-foreground text-sm">Loading audit trail...</span>
              </div>
            </div>
          </div>
        )}

        {isError && (
          <div className="flex items-center justify-center py-12 text-red-500">
            Failed to load audit trail. Please try again.
          </div>
        )}

        {!isLoading && !isError && <AuditTrailTable items={items} />}
      </div>

      {!isLoading && !isError && total > 0 && (
        <AuditTrailPagination
          total={total}
          limit={limit}
          skip={skip}
          onPageChange={(newSkip) => setFilters((prev) => ({ ...prev, skip: newSkip }))}
          onPageSizeChange={(newLimit) =>
            setFilters((prev) => ({ ...prev, limit: newLimit, skip: 0 }))
          }
        />
      )}
    </div>
  );
}
